/**
 * Stripe Connect self-enrollment — the owner portal's first WRITE path.
 *
 * Five states on `connect.status`, same branching shape PayRent.tsx already
 * uses for tenant ACH: a clear next action per state, nothing more. The
 * Account Link is minted fresh on every "Enroll"/"Continue"/"Fix now" click
 * (never cached) — CLAUDE.md is explicit that Account Links are single-use
 * and short-lived, so re-minting on demand is what makes "resend" a button
 * instead of a support request.
 *
 * The return URL is not a success signal (also CLAUDE.md, also explicit):
 * this page never marks itself enrolled from a query param or a redirect —
 * only `connect.status` from the server, which only ever changes via the
 * account.updated webhook path (plus the two staff/owner-initiated status
 * bumps in connect-service.ts).
 */

import { useState } from 'react';
import { useOne } from '@refinedev/core';
import type { OwnerConnectStatus } from 'shared';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { api, ApiError } from '../../../lib/api.js';

interface OwnerRecord {
  connect: {
    status: OwnerConnectStatus;
    requirementsDue: string[];
    enrolledAt: string | null;
  };
}

export function OwnerPayoutsPage() {
  const { state } = useSession();
  const ownerId = state.status === 'signed-in' ? state.me.actor.ownerId : null;

  // No explicit refetch call: the return URL is not a success signal, so
  // there is nothing to refetch on return from Stripe — status only ever
  // changes via the account.updated webhook path, and Refine's own
  // cache invalidation on window refocus picks that up on its own.
  const { data, isLoading } = useOne<OwnerRecord>({
    resource: 'owners',
    id: ownerId ?? '',
    queryOptions: { enabled: Boolean(ownerId) },
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startOrContinueEnrollment() {
    setBusy(true);
    setError(null);
    try {
      const { accountLinkUrl } = await api<{ accountLinkUrl: string }>('/owner-connect/enroll', {
        method: 'POST',
      });
      window.location.href = accountLinkUrl;
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not start enrollment. Try again in a moment.',
      );
      setBusy(false);
    }
  }

  if (state.status !== 'signed-in') return null;

  const connect = data?.data.connect;

  return (
    <section>
      <h1>Payouts</h1>
      <p className="muted">
        Set up direct deposit for your rental income. This is a short, secure setup process hosted
        by Stripe, our payments partner — we never see your bank account or routing number.
      </p>
      {error ? <p className="alert alert--error">{error}</p> : null}

      <Bento>
        {isLoading || !connect ? (
          <BentoCell span="full">
            <p className="bento__hint">Loading…</p>
          </BentoCell>
        ) : connect.status === 'NOT_INVITED' || connect.status === 'INVITED' ? (
          <BentoCell span="full" title="Set up direct deposit">
            <p className="bento__hint">
              You haven't set up direct deposit yet. It only takes a few minutes.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Starting…' : 'Enroll'}
            </button>
          </BentoCell>
        ) : connect.status === 'ONBOARDING_STARTED' || connect.status === 'PENDING_VERIFICATION' ? (
          <BentoCell span="full" title="Finish setting up direct deposit">
            <p className="bento__hint">
              Your setup is in progress. Pick up where you left off to finish verification.
            </p>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Loading…' : 'Continue'}
            </button>
          </BentoCell>
        ) : connect.status === 'REQUIREMENTS_DUE' ? (
          <BentoCell span="full" tone="attention" title="Action needed on your payout account">
            <p className="bento__hint">
              Stripe needs updated information before your next payout can go through.
            </p>
            {connect.requirementsDue.length > 0 ? (
              <ul className="bento__sub">
                {connect.requirementsDue.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void startOrContinueEnrollment()}
            >
              {busy ? 'Loading…' : 'Fix now'}
            </button>
          </BentoCell>
        ) : (
          <BentoCell span="full" title="Direct deposit is set up">
            <p className="bento__hint">
              Your account is enrolled
              {connect.enrolledAt ? ` since ${connect.enrolledAt.slice(0, 10)}` : ''}. Disbursements
              will be transferred directly to your bank account.
            </p>
          </BentoCell>
        )}
      </Bento>
    </section>
  );
}
